import * as THREE from 'three';

/** Registered objects whose world-bounds centres project inside a CSS-pixel rectangle. */
export function idsInsideScreenRect({ viewport, registry, rect }) {
  const canvas = viewport.renderer.domElement.getBoundingClientRect();
  const left = Math.min(rect.left, rect.right);
  const right = Math.max(rect.left, rect.right);
  const top = Math.min(rect.top, rect.bottom);
  const bottom = Math.max(rect.top, rect.bottom);
  const bounds = new THREE.Box3();
  const point = new THREE.Vector3();
  const ids = [];

  for (const { id } of registry.list()) {
    const object = registry.get(id);
    if (!object) continue;
    // Same stale-ancestor gap fixed for focusObject/group()/frameSelection:
    // Box3.setFromObject refreshes the object's own subtree but not a stale
    // ancestor's matrixWorld.
    object.updateWorldMatrix(true, false);
    bounds.setFromObject(object);
    if (bounds.isEmpty()) object.getWorldPosition(point);
    else bounds.getCenter(point);
    point.project(viewport.camera);
    if (!Number.isFinite(point.z) || point.z < -1 || point.z > 1) continue;
    const x = canvas.left + ((point.x + 1) / 2) * canvas.width;
    const y = canvas.top + ((1 - point.y) / 2) * canvas.height;
    if (x >= left && x <= right && y >= top && y <= bottom) ids.push(id);
  }
  return ids;
}

/**
 * Connects the persistent selection model to the rendered scene.
 */
export function createSelectionView({ viewport, registry, state, onFrame = () => {} } = {}) {
  const raycaster = new THREE.Raycaster();
  const pointer = new THREE.Vector2();
  const helpers = new THREE.Group();
  helpers.name = 'Selection helpers';
  viewport.helpers.add(helpers);
  let tool = null;
  let pointerStart = null;
  let ignoreNextClick = false;

  function disposeHelpers() {
    for (const helper of [...helpers.children]) {
      helpers.remove(helper);
      helper.geometry?.dispose();
      helper.material?.dispose();
    }
  }

  function refresh() {
    state.prune();
    disposeHelpers();
    for (const id of state.ids) {
      const object = registry.get(id);
      if (!object) continue;
      const color = id === state.activeId ? 0xffb020 : 0x4f9de0;
      // Same stale-ancestor gap as idsInsideScreenRect above: BoxHelper and
      // setFromObject refresh the object's own subtree but not a moved parent.
      object.updateWorldMatrix(true, false);
      helpers.add(new THREE.BoxHelper(object, color));
      if (id === state.activeId) {
        const objectBounds = new THREE.Box3().setFromObject(object);
        const markerSize = THREE.MathUtils.clamp(
          objectBounds.getSize(new THREE.Vector3()).length() * 0.04,
          0.06,
          0.24,
        );
        const marker = new THREE.Mesh(
          new THREE.OctahedronGeometry(markerSize),
          new THREE.MeshBasicMaterial({ color: 0xffb020, depthTest: false }),
        );
        marker.name = 'Active selection marker';
        marker.position.copy(objectBounds.max);
        marker.renderOrder = 1000;
        helpers.add(marker);
      }
    }
  }

  function idAt(event) {
    const bounds = viewport.renderer.domElement.getBoundingClientRect();
    pointer.x = ((event.clientX - bounds.left) / bounds.width) * 2 - 1;
    pointer.y = -((event.clientY - bounds.top) / bounds.height) * 2 + 1;
    raycaster.setFromCamera(pointer, viewport.camera);

    const hit = raycaster.intersectObjects(viewport.content.children, true)[0];
    for (let object = hit?.object; object && object !== viewport.content; object = object.parent) {
      if (object.userData.anvilId) return object.userData.anvilId;
    }
    return null;
  }

  function selectAt(event) {
    if (event.button != null && event.button !== 0) return;
    if (ignoreNextClick) {
      ignoreNextClick = false;
      return;
    }
    if (tool) return;
    const id = idAt(event);
    if (id) state.select(id, { toggle: event.shiftKey });
    else if (!event.shiftKey) state.clear();
  }

  function frameAt(event) {
    if (tool) return;
    const id = idAt(event);
    if (!id) return;
    state.select(id);
    onFrame(id);
  }

  function startPointer(event) {
    if (event.button !== 0) return;
    ignoreNextClick = false;
    pointerStart = { id: event.pointerId, x: event.clientX, y: event.clientY, moved: false };
  }

  function trackPointer(event) {
    if (!pointerStart || event.pointerId !== pointerStart.id) return;
    pointerStart.moved ||= Math.hypot(event.clientX - pointerStart.x, event.clientY - pointerStart.y) > 4;
  }

  function trackControlChange() {
    if (pointerStart) pointerStart.moved = true;
  }

  function finishPointer(event) {
    if (!pointerStart || event.pointerId !== pointerStart.id) return;
    ignoreNextClick = pointerStart.moved;
    pointerStart = null;
  }

  const canvas = viewport.renderer.domElement;
  canvas.addEventListener('pointerdown', startPointer, true);
  canvas.addEventListener('pointermove', trackPointer, true);
  canvas.addEventListener('pointerup', finishPointer, true);
  canvas.addEventListener('pointercancel', finishPointer, true);
  canvas.addEventListener('click', selectAt);
  canvas.addEventListener('dblclick', frameAt);
  viewport.controls.addEventListener('change', trackControlChange);
  refresh();

  return {
    idAt,
    setTool(nextTool) {
      tool = nextTool;
    },
    destroy() {
      canvas.removeEventListener('pointerdown', startPointer, true);
      canvas.removeEventListener('pointermove', trackPointer, true);
      canvas.removeEventListener('pointerup', finishPointer, true);
      canvas.removeEventListener('pointercancel', finishPointer, true);
      canvas.removeEventListener('click', selectAt);
      canvas.removeEventListener('dblclick', frameAt);
      viewport.controls.removeEventListener('change', trackControlChange);
      disposeHelpers();
      helpers.removeFromParent();
    },
    refresh,
  };
}
